import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bot,
  Briefcase,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit3,
  Eye,
  Inbox,
  Layers3,
  Plus,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Target,
  Trash2,
  TrendingUp,
  Trophy,
  X,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/authStore";
import { useToasts } from "../components/Toasts";
import TopBar from "../components/TopBar";
import Modal from "../components/Modal";
import EmptyState from "../components/EmptyState";
import { fmtDateTime, relTime } from "../lib/format";
import "./FocusBentoPage.css";

const BUCKETS = ["NOW", "NEXT", "PARKING_LOT"];
const BUCKET_LABEL = { NOW: "Now", NEXT: "Next", PARKING_LOT: "Parking Lot" };
const ROLES = ["Founder", "Family", "Employee", "Personal"];
const KINDS = [
  { id: "needs_you", label: "Needs You" },
  { id: "waiting_on", label: "Waiting On" },
  { id: "at_risk", label: "At Risk" },
  { id: "ai_can_handle", label: "AI Can Handle" },
  { id: "upcoming", label: "Upcoming" },
  { id: "general", label: "General" },
];
const KIND_TONE = { needs_you: "amber", waiting_on: "dim", at_risk: "red", ai_can_handle: "blue", upcoming: "purple", general: "dim" };
const HEALTH_TONE = { AT_RISK: "red", WATCH: "amber", BUILDING: "blue", HEALTHY: "green", UNKNOWN: "dim" };
const LS_ROLE_KEY = "lw.focus.role";
const LS_KINDS_KEY = "lw.focus.kinds";
const LS_BUCKET_KEY = "lw.focus.activeBucket";

function LincName({ name }) {
  if (!name) return null;
  const idx = name.toLowerCase().lastIndexOf("linc");
  if (idx < 0) return <>{name}</>;
  return <>{name.slice(0, idx)}<span className="linc-accent">{name.slice(idx, idx + 4)}</span>{name.slice(idx + 4)}</>;
}

function Count({ value, tone = "dim" }) {
  return <span className={`pill ${tone}`}>{value ?? 0}</span>;
}

function BentoCard({ title, Icon, count, tone = "dim", note, children, onOpen, openLabel = "Drill in", className = "" }) {
  return (
    <section className={`focus-bento-card ${className}`}>
      <div className="focus-bento-head">
        <div className="focus-bento-title"><span className="focus-bento-icon"><Icon size={16} /></span><div><b>{title}</b>{note ? <small>{note}</small> : null}</div></div>
        <Count value={count} tone={tone} />
      </div>
      <div className="focus-bento-preview">{children}</div>
      {onOpen ? <button className="focus-bento-open" onClick={onOpen}>{openLabel} <Eye size={12} /></button> : null}
    </section>
  );
}

function PreviewRow({ title, meta, tone, badge, to }) {
  const body = (
    <>
      <span><b>{title}</b>{meta ? <small>{meta}</small> : null}</span>
      {badge ? <span className={`pill ${tone || "dim"}`}>{badge}</span> : null}
    </>
  );
  return to ? <Link to={to} className="focus-preview-row">{body}</Link> : <div className="focus-preview-row">{body}</div>;
}

function ItemForm({ initial, businesses, onClose, onSubmit }) {
  const [form, setForm] = useState(() => ({
    title: initial?.title || "",
    notes: initial?.notes || "",
    bucket: initial?.bucket || "NOW",
    kind: initial?.kind || "general",
    role: initial?.role || "",
    business_key: initial?.business_key || "",
    mission_id: initial?.mission_id || "",
    due_date: initial?.due_date || "",
    deadline: initial?.deadline ? initial.deadline.slice(0, 16) : "",
    urgency_score: initial?.urgency_score ?? 50,
    risk_score: initial?.risk_score ?? 50,
  }));
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const submit = () => {
    if (!form.title.trim()) return;
    onSubmit({
      ...form,
      title: form.title.trim(),
      notes: form.notes || null,
      role: form.role || null,
      business_key: form.business_key || null,
      mission_id: form.mission_id || null,
      due_date: form.due_date || null,
      deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
    });
  };

  return (
    <Modal title={initial ? "Edit focus item" : "Quick Capture"} eyebrow="FOCUSLINC" onClose={onClose} testId="focus-bento-item-modal">
      <div className="focus-form-stack">
        <label><small>Title *</small><input autoFocus value={form.title} onChange={(e) => set("title", e.target.value)} /></label>
        <label><small>Notes</small><textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></label>
        <div className="focus-form-grid three">
          <label><small>Bucket</small><select value={form.bucket} onChange={(e) => set("bucket", e.target.value)}>{BUCKETS.map((b) => <option key={b} value={b}>{BUCKET_LABEL[b]}</option>)}</select></label>
          <label><small>Kind</small><select value={form.kind} onChange={(e) => set("kind", e.target.value)}>{KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}</select></label>
          <label><small>Role</small><select value={form.role} onChange={(e) => set("role", e.target.value)}><option value="">— none —</option>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></label>
        </div>
        <div className="focus-form-grid two">
          <label><small>Business</small><select value={form.business_key} onChange={(e) => set("business_key", e.target.value)}><option value="">— none —</option>{businesses.map((b) => <option key={b.key || b.slug} value={b.key || b.slug}>{b.name}</option>)}</select></label>
          <label><small>Mission ID</small><input value={form.mission_id} onChange={(e) => set("mission_id", e.target.value)} /></label>
        </div>
        <div className="focus-form-grid two">
          <label><small>Due date</small><input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} /></label>
          <label><small>Hard deadline</small><input type="datetime-local" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} /></label>
        </div>
        <div className="focus-form-grid two">
          <label><small>Urgency · {form.urgency_score}</small><input type="range" min="0" max="100" value={form.urgency_score} onChange={(e) => set("urgency_score", Number(e.target.value))} /></label>
          <label><small>Risk · {form.risk_score}</small><input type="range" min="0" max="100" value={form.risk_score} onChange={(e) => set("risk_score", Number(e.target.value))} /></label>
        </div>
      </div>
      <div className="modal-actions"><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={submit} disabled={!form.title.trim()}>{initial ? "Save" : "Capture"}</button></div>
    </Modal>
  );
}

function ReasonModal({ mode, item, to, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState(() => {
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });
  const snooze = mode === "snoozed";
  const title = mode === "move" ? `Move to ${BUCKET_LABEL[to]}?` : snooze ? "Snooze item" : "Archive item";

  return (
    <Modal title={title} eyebrow="CONFIRM" onClose={onClose}>
      <p className="hint">{item?.title}</p>
      <label style={{ marginTop: 12 }}><small>Reason *</small><textarea autoFocus rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why?" /></label>
      {snooze ? <label><small>Snooze until</small><input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} /></label> : null}
      <div className="modal-actions"><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" disabled={reason.trim().length < 3 || (snooze && !until)} onClick={() => onSubmit(reason.trim(), snooze ? new Date(until).toISOString() : null)}>Confirm</button></div>
    </Modal>
  );
}

function CompactItem({ item, onEdit, onMove, onStatus, onDelete, onPriority }) {
  const kind = KINDS.find((k) => k.id === item.kind)?.label || item.kind;
  const sourceLink = item.source_ref?.mission_id
    ? `/commandlinc/missions/${item.source_ref.mission_id}`
    : item.source_ref?.business_key
      ? `/businesses/${item.source_ref.business_key}`
      : null;

  return (
    <article className="focus-compact-item">
      <div className="focus-compact-main">
        <div className="focus-compact-tags">
          <span className={`pill ${KIND_TONE[item.kind] || "dim"}`}>{kind}</span>
          {item.role ? <span className="pill dim">{item.role}</span> : null}
          {item.business_key ? <span className="pill dim"><LincName name={item.business_key} /></span> : null}
          {item.source !== "manual" ? <span className="pill dim">AUTO</span> : null}
        </div>
        <b>{item.title}</b>
        <div className="focus-compact-meta">
          <span>urg {item.urgency_score}</span><span>risk {item.risk_score}</span>
          {item.due_date ? <span>due {item.due_date}</span> : null}
          {item.deadline ? <span>deadline {fmtDateTime(item.deadline)}</span> : null}
          {sourceLink ? <Link to={sourceLink}>source ↗</Link> : null}
        </div>
      </div>
      <div className="focus-compact-actions">
        <button className="icon-btn" onClick={() => onPriority(item, -1)} title="Higher priority"><ChevronUp size={13} /></button>
        <button className="icon-btn" onClick={() => onPriority(item, 1)} title="Lower priority"><ChevronDown size={13} /></button>
        <button className="icon-btn" onClick={() => onStatus(item, "done")} title="Done"><Check size={13} /></button>
        <button className="icon-btn" onClick={() => onEdit(item)} title="Edit"><Edit3 size={13} /></button>
        <select value={item.bucket} onChange={(e) => onMove(item, e.target.value)} title="Move bucket">
          {BUCKETS.map((b) => <option key={b} value={b}>{BUCKET_LABEL[b]}</option>)}
        </select>
        <select value="" onChange={(e) => { if (e.target.value) onStatus(item, e.target.value); }} title="More status actions">
          <option value="">More…</option><option value="snoozed">Snooze</option><option value="archived">Archive</option>
        </select>
        {item.source === "manual" ? <button className="icon-btn" onClick={() => onDelete(item)} title="Delete / archive"><Trash2 size={13} /></button> : null}
      </div>
    </article>
  );
}

export default function FocusBentoPage() {
  const { user } = useAuth();
  const pushToast = useToasts((state) => state.push);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [top3, setTop3] = useState(null);
  const [activeBucket, setActiveBucket] = useState(() => localStorage.getItem(LS_BUCKET_KEY) || "NOW");
  const [roleFilter, setRoleFilter] = useState(() => localStorage.getItem(LS_ROLE_KEY) || "ALL");
  const [kindFilter, setKindFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KINDS_KEY) || "[]"); } catch { return []; }
  });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reasonAction, setReasonAction] = useState(null);
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => {
    const [{ data: focusSummary }, { data: focusItems }, { data: businessRows }, { data: ranking }] = await Promise.all([
      api.get("/focus/summary"), api.get("/focus_items?limit=300"), api.get("/businesses"), api.get("/focus/top3"),
    ]);
    setSummary(focusSummary); setItems(focusItems); setBusinesses(businessRows); setTop3(ranking);
  }, []);

  useEffect(() => { load().catch(() => pushToast("FocusLinc could not refresh.", "err")); }, [load, pushToast]);
  useEffect(() => { localStorage.setItem(LS_BUCKET_KEY, activeBucket); }, [activeBucket]);
  useEffect(() => { localStorage.setItem(LS_ROLE_KEY, roleFilter); }, [roleFilter]);
  useEffect(() => { localStorage.setItem(LS_KINDS_KEY, JSON.stringify(kindFilter)); }, [kindFilter]);

  const filtered = useMemo(() => items.filter((item) => {
    if (item.status !== "open") return false;
    if (roleFilter !== "ALL" && item.role !== roleFilter) return false;
    if (kindFilter.length && !kindFilter.includes(item.kind)) return false;
    return true;
  }), [items, roleFilter, kindFilter]);

  const byBucket = useMemo(() => {
    const grouped = { NOW: [], NEXT: [], PARKING_LOT: [] };
    filtered.forEach((item) => grouped[item.bucket]?.push(item));
    BUCKETS.forEach((bucket) => grouped[bucket].sort((a, b) => (a.priority || 0) - (b.priority || 0)));
    return grouped;
  }, [filtered]);

  const createItem = async (payload) => {
    await api.post("/focus_items", payload); setCreating(false); pushToast("Captured.", "ok"); await load();
  };
  const saveEdit = async (payload) => {
    await api.patch(`/focus_items/${editing.id}`, payload); setEditing(null); pushToast("Updated.", "ok"); await load();
  };
  const moveItem = async (item, to, reason = null) => {
    if (item.bucket === to) return;
    if (item.bucket === "NOW" && to !== "NOW" && !reason) { setReasonAction({ mode: "move", item, to }); return; }
    await api.post(`/focus_items/${item.id}/move`, { to_bucket: to, reason }); setReasonAction(null); pushToast(`Moved to ${BUCKET_LABEL[to]}.`, "ok"); await load();
  };
  const statusItem = async (item, status, reason = null, snoozedUntil = null) => {
    if ((status === "snoozed" || status === "archived") && !reason) { setReasonAction({ mode: status, item }); return; }
    const body = { status };
    if (reason) body.reason = reason;
    if (snoozedUntil) body.snoozed_until = snoozedUntil;
    await api.post(`/focus_items/${item.id}/status`, body); setReasonAction(null); pushToast(`Marked ${status}.`, "ok"); await load();
  };
  const deleteItem = async (item) => {
    if (!window.confirm(`Archive “${item.title}”?`)) return;
    try { await api.delete(`/focus_items/${item.id}`); pushToast("Archived.", "ok"); await load(); }
    catch (error) { pushToast(error?.response?.data?.detail?.message || error?.response?.data?.detail || "Archive blocked.", "err"); }
  };
  const priorityItem = async (item, delta) => { await api.patch(`/focus_items/${item.id}`, { priority: (item.priority || 0) + delta }); await load(); };
  const runSync = async () => {
    try { const { data } = await api.post("/focus/sync"); pushToast(`Sync +${data.created} · ${data.updated} updated · ${data.closed} closed`, "ok"); await load(); }
    catch { pushToast("Sync failed.", "err"); }
  };

  if (!summary) return <TopBar title="FocusLinc" subtitle="Loading attention console…" />;

  const needsYou = summary.needs_you || [];
  const atRiskBusinesses = summary.at_risk?.businesses || [];
  const atRiskMissions = summary.at_risk?.missions || [];
  const atRiskCount = atRiskBusinesses.length + atRiskMissions.length;
  const aiItems = summary.ai_can_handle || [];
  const upcoming = summary.upcoming || [];
  const waiting = summary.waiting_on || [];
  const c = summary.cross_business_summary || {};

  const toggleKind = (kind) => setKindFilter((current) => current.includes(kind) ? current.filter((x) => x !== kind) : [...current, kind]);

  return (
    <div className="focus-bento-page" data-edit-name="FocusLinc attention">
      <TopBar
        title={<><span className="linc-accent">Focus</span>Linc</>}
        subtitle={`Attention console · ${user?.name || "Director"}`}
        actions={<><Link className="btn small" to="/focus/classic"><Layers3 size={12} /> Full view</Link><button className="btn small" onClick={runSync}><RefreshCw size={12} /> Sync</button><button className="btn small primary" onClick={() => setCreating(true)}><Plus size={12} /> Capture</button></>}
      />

      <div className="focus-question-rail" data-edit-name="FocusLinc questions">
        {["What needs me now?", "What am I waiting on?", "What is at risk?", "What can AI handle?"].map((q) => <span key={q}><Sparkles size={11} /> {q}</span>)}
      </div>

      <section className="focus-top3-compact" data-edit-name="Today's Top 3">
        <div className="focus-section-label"><Trophy size={13} /> TODAY&apos;S TOP 3 <small>Attention Engine · {top3?.algorithm_version || "v1"}</small></div>
        <div className="focus-top3-strip">
          {(top3?.top_3 || []).length === 0 ? <div className="focus-empty-inline">Add something to NOW to begin ranking.</div> : (top3.top_3 || []).map((ranked, index) => (
            <div className="focus-top3-mini" key={ranked.item.id}>
              <span className="focus-rank">{index + 1}</span>
              <div><b>{ranked.item.title}</b><small>{ranked.reasons?.[0] || `score ${ranked.score}`}</small></div>
              <span className="pill dim">{ranked.score}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="focus-bento-grid" data-edit-name="FocusLinc boards">
        <BentoCard title="Needs You" Icon={Inbox} count={needsYou.length} tone={needsYou.length ? "amber" : "green"} note="Approvals requiring a decision" onOpen={() => setDrill("needs")}>
          {needsYou.length ? needsYou.slice(0, 2).map((a) => <PreviewRow key={a.approval_id} title={a.mission_title || "Untitled mission"} meta={`${a.stage_key} · ${relTime(a.requested_at)}`} badge="pending" tone="amber" to={`/commandlinc/missions/${a.mission_id}`} />) : <div className="focus-clear-state"><Check size={15} /> Inbox clear</div>}
        </BentoCard>

        <BentoCard title="At Risk" Icon={ShieldAlert} count={atRiskCount} tone={atRiskCount ? "red" : "green"} note="Blocked or slipping" onOpen={() => setDrill("risk")}>
          {atRiskCount ? <>{atRiskBusinesses.slice(0, 1).map((b) => <PreviewRow key={b.key} title={b.name} meta={b.target_date ? `target ${b.target_date}` : "business"} badge={b.health} tone={HEALTH_TONE[b.health]} to={`/businesses/${b.key}`} />)}{atRiskMissions.slice(0, 1).map((m) => <PreviewRow key={m.id} title={m.title} meta={`stage ${m.current_stage}`} badge="blocked" tone="red" to={`/commandlinc/missions/${m.id}`} />)}</> : <div className="focus-clear-state"><Check size={15} /> Nothing at risk</div>}
        </BentoCard>

        <BentoCard title="Waiting On" Icon={Clock} count={waiting.length} note="External follow-ups" onOpen={() => setDrill("waiting")}>
          {waiting.length ? waiting.slice(0, 2).map((w, i) => <PreviewRow key={w.id || i} title={w.title || "Waiting item"} meta={w.detail} />) : <div className="focus-muted-state">Comms sync is queued; no external waiting items yet.</div>}
        </BentoCard>

        <BentoCard title="AI Can Handle" Icon={Bot} count={aiItems.length} tone="blue" note="Work moving without you" onOpen={() => setDrill("ai")}>
          {aiItems.length ? aiItems.slice(0, 2).map((m) => <PreviewRow key={m.id} title={m.title} meta={`stage ${m.current_stage}`} badge="running" tone="blue" to={`/commandlinc/missions/${m.id}`} />) : <div className="focus-muted-state">No AI stages in flight.</div>}
        </BentoCard>

        <BentoCard title="Businesses" Icon={Briefcase} count={(summary.businesses || []).length} tone="purple" note="One doorway to every venture" onOpen={() => setDrill("businesses")}>
          {(summary.businesses || []).slice(0, 2).map((b) => <PreviewRow key={b.key} title={<LincName name={b.name} />} meta={`${b.status} · ${(b.business_type || "").replace(/_/g, " ")}`} badge={b.health} tone={HEALTH_TONE[b.health]} to={`/businesses/${b.key}`} />)}
        </BentoCard>

        <BentoCard title="Upcoming" Icon={CalendarClock} count={upcoming.length} note="Dates inside 60 days" onOpen={() => setDrill("upcoming")}>
          {upcoming.length ? upcoming.slice(0, 2).map((u) => <PreviewRow key={u.key} title={<LincName name={u.name} />} meta={u.current_milestone} badge={u.target_date} to={`/businesses/${u.key}`} />) : <div className="focus-muted-state">Nothing due soon.</div>}
        </BentoCard>

        <BentoCard title="Cross-business Pulse" Icon={TrendingUp} count={(c.in_progress || 0) + (c.waiting_review || 0) + (c.blocked || 0)} note="Portfolio activity" className="focus-bento-wide" onOpen={() => setDrill("pulse")}>
          <div className="focus-stat-row"><span>In progress <b>{c.in_progress ?? 0}</b></span><span>Review <b>{c.waiting_review ?? 0}</b></span><span>Blocked <b>{c.blocked ?? 0}</b></span><span>Done 7d <b>{c.completed_last_7d ?? 0}</b></span></div>
        </BentoCard>
      </div>

      <section className="focus-planning-compact">
        <div className="focus-planning-head">
          <div><div className="focus-section-label"><Target size={13} /> ATTENTION QUEUE</div><small>One bucket at a time. Drill instead of doom-scroll.</small></div>
          <div className="focus-filter-inline">
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} aria-label="Role filter"><option value="ALL">All roles</option>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select>
          </div>
        </div>
        <div className="focus-kind-row">{KINDS.map((k) => <button key={k.id} className={`chip ${kindFilter.includes(k.id) ? "active" : ""}`} onClick={() => toggleKind(k.id)}>{k.label}</button>)}{kindFilter.length ? <button className="chip clear" onClick={() => setKindFilter([])}><X size={10} /> Clear</button> : null}</div>
        <div className="focus-bucket-tabs">{BUCKETS.map((bucket) => <button key={bucket} className={activeBucket === bucket ? "active" : ""} onClick={() => setActiveBucket(bucket)}><span>{BUCKET_LABEL[bucket]}</span><Count value={byBucket[bucket].length} tone={bucket === "NOW" ? "amber" : "dim"} /></button>)}</div>
        <div className="focus-active-bucket">
          {byBucket[activeBucket].length === 0 ? <EmptyState title={`${BUCKET_LABEL[activeBucket]} is clear.`} icon={Target} /> : byBucket[activeBucket].map((item) => <CompactItem key={item.id} item={item} onEdit={setEditing} onMove={moveItem} onStatus={statusItem} onDelete={deleteItem} onPriority={priorityItem} />)}
        </div>
      </section>

      <button className="focus-fab" onClick={() => setCreating(true)} aria-label="Quick capture"><Plus size={22} /></button>

      {creating ? <ItemForm businesses={businesses} onClose={() => setCreating(false)} onSubmit={(payload) => createItem(payload).catch(() => pushToast("Create failed.", "err"))} /> : null}
      {editing ? <ItemForm initial={editing} businesses={businesses} onClose={() => setEditing(null)} onSubmit={(payload) => saveEdit(payload).catch(() => pushToast("Update failed.", "err"))} /> : null}
      {reasonAction ? <ReasonModal mode={reasonAction.mode} item={reasonAction.item} to={reasonAction.to} onClose={() => setReasonAction(null)} onSubmit={(reason, until) => {
        const task = reasonAction.mode === "move" ? moveItem(reasonAction.item, reasonAction.to, reason) : statusItem(reasonAction.item, reasonAction.mode, reason, until);
        task.catch((error) => pushToast(error?.response?.data?.detail || "Action failed.", "err"));
      }} /> : null}
      {drill ? <DrillModal type={drill} summary={summary} onClose={() => setDrill(null)} /> : null}
    </div>
  );
}

function DrillModal({ type, summary, onClose }) {
  const rows = {
    needs: (summary.needs_you || []).map((a) => ({ key: a.approval_id, title: a.mission_title || "Untitled mission", meta: `${a.stage_key} · ${relTime(a.requested_at)}`, badge: "pending", tone: "amber", to: `/commandlinc/missions/${a.mission_id}` })),
    risk: [
      ...(summary.at_risk?.businesses || []).map((b) => ({ key: `b-${b.key}`, title: b.name, meta: b.target_date ? `target ${b.target_date}` : "business", badge: b.health, tone: HEALTH_TONE[b.health], to: `/businesses/${b.key}` })),
      ...(summary.at_risk?.missions || []).map((m) => ({ key: `m-${m.id}`, title: m.title, meta: `stage ${m.current_stage}`, badge: "blocked", tone: "red", to: `/commandlinc/missions/${m.id}` })),
    ],
    waiting: (summary.waiting_on || []).map((w, i) => ({ key: w.id || i, title: w.title || "Waiting item", meta: w.detail })),
    ai: (summary.ai_can_handle || []).map((m) => ({ key: m.id, title: m.title, meta: `stage ${m.current_stage}`, badge: "running", tone: "blue", to: `/commandlinc/missions/${m.id}` })),
    businesses: (summary.businesses || []).map((b) => ({ key: b.key, title: b.name, meta: `${b.status} · ${(b.business_type || "").replace(/_/g, " ")}`, badge: b.health, tone: HEALTH_TONE[b.health], to: `/businesses/${b.key}` })),
    upcoming: (summary.upcoming || []).map((u) => ({ key: u.key, title: u.name, meta: u.current_milestone, badge: u.target_date, tone: "dim", to: `/businesses/${u.key}` })),
  };
  const titles = { needs: "Needs You", risk: "At Risk", waiting: "Waiting On", ai: "AI Can Handle", businesses: "Businesses & Tools", upcoming: "Upcoming", pulse: "Cross-business Pulse" };
  const c = summary.cross_business_summary || {};

  return (
    <Modal title={titles[type] || "Drill In"} eyebrow="FOCUSLINC" onClose={onClose}>
      {type === "pulse" ? <div className="focus-drill-stats"><PreviewRow title="In progress" badge={c.in_progress ?? 0} tone="blue" /><PreviewRow title="Waiting review" badge={c.waiting_review ?? 0} tone="amber" /><PreviewRow title="Blocked" badge={c.blocked ?? 0} tone="red" /><PreviewRow title="Completed last 7d" badge={c.completed_last_7d ?? 0} tone="green" /></div> : (rows[type] || []).length ? <div className="focus-drill-list">{(rows[type] || []).map((row) => <PreviewRow key={row.key} {...row} />)}</div> : <div className="focus-muted-state" style={{ padding: 20 }}>Nothing here yet.</div>}
      <div className="modal-actions"><button className="btn" onClick={onClose}>Close</button>{type === "needs" ? <Link className="btn primary" to="/commandlinc/approvals">Approval inbox</Link> : type === "businesses" ? <Link className="btn primary" to="/businesses">All businesses</Link> : null}</div>
    </Modal>
  );
}
